import { AlertTriangle, CalendarDays, Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { useM3ImpostaDataDocumenti } from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type { PaccoDetail } from "@/types/api";

/**
 * La data di spedizione: quella che finisce stampata su tutti i documenti del
 * plico (il certificato sanitario la usa se non ne ha una sua) e che l'agenzia
 * controlla prima che la merce parta — non la data di generazione, non quella
 * della nota. Salvarla la riporta anche come data di ritiro sul tabellone.
 *
 * È vincolante: finché non c'è, il pacchetto non si genera. Quindi sta in cima
 * al drawer, prima della merce e prima dei documenti, ed è la prima cosa che
 * si vede aprendo un pacchetto che aspetta.
 */

const ITA = (iso: string) => {
  const [a, m, g] = iso.split("-");
  return `${g}/${m}/${a}`;
};

const ORIGINE_PROPOSTA: Record<string, string> = {
  confermata: "dal ritiro confermato",
  attesa: "dal ritiro previsto, ancora da confermare",
  partenza_prevista: "dalla partenza prevista",
};

export function DataDocumenti({ pacco }: { pacco: PaccoDetail }) {
  const salva = useM3ImpostaDataDocumenti(pacco.carico_id);
  const [valore, setValore] = useState(pacco.data_documenti ?? "");

  useEffect(() => {
    setValore(pacco.data_documenti ?? "");
  }, [pacco.data_documenti]);

  // Il pacchetto non ha documenti da generare: la data non gli serve.
  const serve = pacco.manca_data_documenti || pacco.data_documenti != null;
  if (!serve) return null;

  const manca = pacco.data_documenti == null;
  const proposta = pacco.data_documenti_proposta;
  const sporco = valore !== (pacco.data_documenti ?? "");

  return (
    <section
      className={cn(
        "rounded-lg border p-3",
        manca ? "border-amber-300 bg-amber-50" : "border-border bg-card",
      )}
    >
      <div className="flex items-center gap-2">
        {manca ? (
          <AlertTriangle className="h-4 w-4 flex-none text-amber-600" />
        ) : (
          <CalendarDays className="h-4 w-4 flex-none text-brand-700" />
        )}
        <h4 className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
          Data di spedizione
        </h4>
        {!manca && (
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
            sui documenti: {ITA(pacco.data_documenti!)}
          </span>
        )}
      </div>

      <p className={cn("mt-1 text-[12px]", manca ? "text-amber-900" : "text-muted-foreground")}>
        {manca ? (
          <>
            È la data che va stampata su tutti i documenti del plico e che l'agenzia controlla
            prima della partenza. <b>Finché non la metti, il pacchetto non viene generato.</b>
          </>
        ) : (
          <>
            Stampata su tutti i documenti del plico e riportata come data di ritiro sul
            tabellone. Cambiarla rigenera il pacchetto.
            {pacco.data_certificato_sanitario &&
              pacco.data_certificato_sanitario !== pacco.data_documenti && (
                <>
                  {" "}
                  Il certificato sanitario porta una data sua:{" "}
                  <b>{ITA(pacco.data_certificato_sanitario)}</b>.
                </>
              )}
          </>
        )}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={valore}
          onChange={(e) => setValore(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1 text-[13px] focus:border-brand-500 focus:outline-none"
        />
        {proposta && proposta !== valore && (
          <button
            type="button"
            onClick={() => setValore(proposta)}
            className="rounded-md border border-border px-2 py-1 text-[12px] text-muted-foreground hover:border-brand-500 hover:text-brand-700"
            title={`Data ${ORIGINE_PROPOSTA[pacco.data_documenti_proposta_tipo ?? ""] ?? ""}`}
          >
            usa il {ITA(proposta)}{" "}
            <span className="text-[11px]">
              ({ORIGINE_PROPOSTA[pacco.data_documenti_proposta_tipo ?? ""] ?? "dal tabellone"})
            </span>
          </button>
        )}
        <div className="flex-1" />
        {pacco.data_documenti && !sporco && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            disabled={salva.isPending}
            onClick={() => salva.mutate(null)}
          >
            Togli la data
          </Button>
        )}
        <Button size="sm" disabled={!valore || !sporco || salva.isPending} onClick={() => salva.mutate(valore)}>
          {salva.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Rigenero il pacchetto…
            </>
          ) : manca ? (
            <>
              <Check className="mr-1.5 h-3.5 w-3.5" /> Salva e genera il pacchetto
            </>
          ) : (
            "Salva e rigenera"
          )}
        </Button>
      </div>
    </section>
  );
}
